import React, { Component } from 'react';
import PropTypes from 'prop-types';

import AssetAvailability from '@components/shared/asset_availability/main';
import RadioButton from '@components/common/radio_button/main';
import {
  bulkRefreshSections,
  Collection,
  pluralizeObject
} from '@helpers/show_page_helpers';
import getAssets from '@api/v4/assets/assets';
import getAttachments from '@api/v4/assets/attachments';
import { postToggleApproval } from '@api/v4/bulk_actions/assets';
import StatusContent from './status_content';
import AttachmentSidebar from '../shared/attachment_sidebar/main';

import './styles/main.scss';

class StatusContainer extends Component {
  state = {
    activeCdnTracers: null,
    approved: null,
    asset: null,
    assetKey: this.props.assetKey,
    attachments: [],
    attachmentsLoading: true,
    availability: null,
    availabilityStart: null,
    availabilityEnd: null,
    displayComments: BFG.context.hasFeature('comments'),
    displaySidebarAsset: !(!this.props.displayAssetStatus && !BFG.context.hasFeature('comments')),
    displayAssetStatus: this.props.displayAssetStatus,
    featureComments: BFG.context.hasFeature('comments'),
    fetchControllers: [],
    visibleAttachment: {},
  }

  componentDidMount() {
    this.retrieveAsset(undefined, true); // (visibleAttachment, initialLoad)
  }

  updateFetchControllers = (controller) => {
    this.setState((prevState) => ({ fetchControllers: [...prevState.fetchControllers, controller] }));
  }

  abortFetchControllers = () => {
    this.state.fetchControllers.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
  }

  updateSideBarResources = (visibleAttachment) => {
    this.abortFetchControllers();

    if (this.state.attachments[0].id === visibleAttachment.id) {
      this.retrieveAsset(visibleAttachment, undefined);
    } else {
      this.retrieveAttachments(visibleAttachment, undefined);
    }
  }

  retrieveAsset = (visibleAttachment, initialLoad) => {
    const { assetKey } = this.state;
    const options = {
      assetKey,
      params: { fields: 'availability,availability_end,availability_start,active_cdn_tracers,thumbnail_url' }
    };

    getAssets(options, this.updateFetchControllers)
      .then((response) => {
        if (response.data) {
          const asset = response.data;

          this.setState({
            activeCdnTracers: asset.attributes.active_cdn_tracers,
            approved: asset.attributes.approved,
            asset,
            availability: asset.attributes.availability,
            availabilityEnd: asset.attributes.availability_end,
            availabilityStart: asset.attributes.availability_start
          }, () => {
            this.retrieveAttachments(visibleAttachment || {}, initialLoad);
          });
        } else { // error state
          this.setState({ attachmentsLoading: false });
        }
      })
      .catch(() => this.setState({ attachmentsLoading: false }));
  }

  retrieveAttachments = (visibleAttachment, initialLoad) => {
    const { assetKey } = this.state;
    const options = {
      assetKey,
      params: { fields: 'download_url,thumbnail_url,extension,thumbnailed' }
    };
    let attachment = visibleAttachment;

    getAttachments(options, this.updateFetchControllers)
      .then((response) => {
        if (response.data) {
          const sortedAttachments = response.data.sort((a, b) => (
            a.attributes.position - b.attributes.position
          ));

          // if initial load and not showing asset in sidebar, select first attachment
          if (initialLoad && !this.state.displaySidebarAsset) {
            attachment = sortedAttachments[0];
          }

          this.setState({
            attachments: sortedAttachments,
            visibleAttachment: attachment || {}
          });
        }
        this.setState({ attachmentsLoading: false });
      })
      .catch(() => this.setState({ attachmentsLoading: false }));
  }

  makeNotification = (approvalVerb, bodyText) => {
    const { asset } = this.state;
    const selectedAssetKeys = [];
    selectedAssetKeys.push(asset.key);
    const assetQtyObject = pluralizeObject(selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? t('assets') : t('asset');

    Notify.create({
      title: `${approvalVerb} ${assetQtyObject.qty} ${assetCopy}!`,
      body: bodyText,
      type: 'success'
    });
  }

  updateAssetApproval = () => {
    const { activeCdnTracers } = this.state;
    const that = this;

    if (this.state.approved) {
      if (activeCdnTracers && activeCdnTracers > 0) {
        const sweet_alert_options = {
          title: t('This asset has external links.'),
          text: t('Are you sure you want to unapprove? Unapproving will cause broken image links on sites that depend on the availability of this asset. Check the usage tab to see active external links before unapproving.\n\n'),
          type: 'warning',
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Unapprove',
          closeOnConfirm: true,
          customClass: 'merging'
        };

        window.swal(sweet_alert_options, (unapprove_selected) => {
          if (unapprove_selected) {
            that.toggleApproval('unapprove');
          }
        });
      } else {
        this.toggleApproval('unapprove');
      }
    } else {
      this.toggleApproval('approve');
    }
  }

  toggleApproval = (approvalType) => {
    const asset = Object.assign({}, this.state.asset);
    const options = {
      approvalType,
      data: { asset_keys: [asset.id] },
      context: this
    };

    postToggleApproval(options, this.updateFetchControllers)
      .then((response) => {
        if (response && !response.ok) {
          this.setState((state) => ({
            approved: !state.approved,
            availability: approvalType === 'approve' ? 'approved' : 'pending_approval'
          }));
          bulkRefreshSections([asset.id]);
          const approvalQty = response.required_approvals;
          const approvalCopy = approvalQty === 1 ? t('total approval') : t('total approvals');
          const bodyText = (approvalQty === 'N/A' || !approvalQty)
            ? ''
            : `${t('This asset requires')} ${approvalQty} ${approvalCopy}. ${approvalType === 'approve' ? t('All previous approvals are void.') : ''}`;
          this.makeNotification(approvalType === 'approve' ? t('Approved') : t('Unapproved'), bodyText);
        } else {
          const approvingErrorCopy = t('An error occurred when approving assets.');
          const unapprovingErrorCopy = t('An error occurred when unapproving assets.');
          Notify.create({
            title: `${approvalType === 'approve' ? approvingErrorCopy : unapprovingErrorCopy} ${t('Please try again.')}`,
            type: 'error'
          });
        }
      });
  }

  updateAvailabilityStatus = (assetAvailability) => {
    let { newAvailability } = this.state;
    const start = assetAvailability.availability_start;
    const end = assetAvailability.availability_end;

    if (newAvailability === 'available') {
      if (start && new Date(start) > new Date()) {
        newAvailability = 'unpublished';
      }
      if (end && new Date(end) < new Date()) {
        newAvailability = 'expired';
      }
    }

    this.setState({
      availabilityStart: assetAvailability.availability_start,
      availabilityEnd: assetAvailability.availability_end,
      availability: newAvailability
    });
  }

  renderStatus = () => {
    const {
      availability, availabilityEnd, availabilityStart, displayAssetStatus, visibleAttachment
    } = this.state;

    if (!displayAssetStatus || Object.keys(visibleAttachment).length) { return undefined; }

    let status;
    switch (availability) {
      case 'approved':
        status = t(' Approved');
        break;
      case 'pending_approval':
        status = t(' Pending approval');
        break;
      case 'unpublished':
        status = t(' Draft');
        break;
      case 'expired':
        status = t(' Expired');
        break;
      default:
        status = t(' Available');
    }

    if (status === ' Approved' || ' Pending approval' || ' Available') {
      if (availabilityStart && new Date(availabilityStart) > new Date()) {
        status += t(' and unpublished');
      }

      if (availabilityEnd && new Date(availabilityEnd) < new Date()) {
        status += t(' and expired');
      }
    }

    return (
      <div className="asset-status">
        <p>
          {t('Current status:')}
          <span className="status">
            {status}
          </span>
        </p>
        <hr />
      </div>
    );
  }

  renderAvailability = () => {
    const {
      activeCdnTracers, asset, assetKey, availabilityEnd, availabilityStart, visibleAttachment
    } = this.state;

    if (!asset || Object.keys(visibleAttachment).length) { return undefined; }

    if (!Collection.key && BFG.context.hasFeature('asset_availability')) {
      return (
        <React.Fragment>
          <AssetAvailability
            availabilityEnd={availabilityEnd}
            availabilityStart={availabilityStart}
            selectedAssetKeys={new Set([assetKey])}
            updateAvailabilityStatus={this.updateAvailabilityStatus}
            activeCdnTracers={activeCdnTracers}
          />
          <hr />
        </React.Fragment>
      );
    }

    return undefined;
  }

  renderApproval = () => {
    const { approver } = this.props;
    const { approved, visibleAttachment } = this.state;

    if (Object.keys(visibleAttachment).length) { return undefined; }

    if (approver && BFG.context.hasFeature('asset_approvals')) {
      return (
        <div>
          <div className="asset-approvals">
            <div className="radio-button-container">
              <h3>{t('Approval Status:')}</h3>
              <RadioButton
                checkedButton={approved ? "approve" : "unapprove"}
                labelCopy={t("Approved")}
                name="status-approval"
                onChangeFunction={this.updateAssetApproval}
                valueCopy="approve"
              />
              <RadioButton
                checkedButton={approved ? "approve" : "unapprove"}
                labelCopy={t("Unapproved")}
                name="status-approval"
                onChangeFunction={this.updateAssetApproval}
                valueCopy="unapprove"
              />
            </div>
          </div>
        </div>
      );
    }

    return undefined;
  }

  renderStatusContent = () => {
    const { asset, visibleAttachment } = this.state;

    if (!Object.keys(visibleAttachment).length) { return undefined; }

    return (
      <StatusContent
        asset={asset}
        updateMainState={this.setState}
        updateSideBarResources={this.updateSideBarResources}
        visibleAttachment={visibleAttachment}
      />
    );
  }

  renderComments = (hideAssetStatus) => {
    const { displayComments } = this.state;

    if (displayComments) {
      $('div.comments-container').show();

      if (hideAssetStatus === 'hide-asset-status') {
        $('div.comments-container').addClass('comments-full-width');
        $('div.comments-container').removeClass('comments-partial-width');
      } else {
        $('div.comments-container').addClass('comments-partial-width');
        $('div.comments-container').removeClass('comments-full-width');
      }
    } else {
      $('div.comments-container').hide();
      $('div.comments-container').removeClass('comments-full-width');
      $('div.comments-container').removeClass('comments-partial-width');
    }
  }

  updateActiveCard = ({ id, type }) => {
    this.setState((prevState) => {
      let visibleAttachment;

      if (type === 'attachment') {
        const attachments = [...prevState.attachments];
        visibleAttachment = attachments.filter((attachment) => attachment.id === id)[0];
      } else {
        visibleAttachment = {};
      }

      return { visibleAttachment };
    });
  }

  render() {
    const {
      asset,
      attachments,
      displayAssetStatus,
      displayComments,
      displaySidebarAsset,
      attachmentsLoading,
      featureComments,
      visibleAttachment
    } = this.state;
    const hideAssetStatus = !displayAssetStatus && !Object.keys(visibleAttachment).length
      ? 'hide-asset-status'
      : '';
    const showComments = displayComments ? 'show-comments' : '';
    let activeCard = null;
    if (Object.keys(visibleAttachment).length) {
      activeCard = { id: visibleAttachment.id, type: 'attachment' };
    } else if (asset) {
      activeCard = { id: asset.id, type: 'asset' };
    }

    return (
      <React.Fragment>
        <AttachmentSidebar
          asset={asset}
          attachments={attachments}
          attachmentsLoading={attachmentsLoading}
          commentsFeature={featureComments}
          displayAssetCard={displaySidebarAsset}
          updateActiveCard={this.updateActiveCard}
          activeCard={activeCard}
        />
        <div className={`status-container ${hideAssetStatus} ${showComments}`}>
          { this.renderComments(hideAssetStatus) }
          { this.renderStatus() }
          { this.renderAvailability() }
          { this.renderApproval() }
          { this.renderStatusContent() }
        </div>
      </React.Fragment>
    );
  }
}

StatusContainer.propTypes = {
  approver: PropTypes.bool.isRequired,
  assetKey: PropTypes.string.isRequired,
  displayAssetStatus: PropTypes.bool.isRequired
};

export default StatusContainer;
